import { useQuery } from '@tanstack/react-query';
import {
  getDashboardOptions,
  getOutputMetricsOptions,
  listDashboardsOptions,
  listPinnedWidgetsOptions,
  listWidgetsOptions,
  queryOutputsOptions,
} from '../generated/api/@tanstack/react-query.gen.js';

export function useRunOutputs(runId: string | number | null) {
  const query = useQuery({
    ...queryOutputsOptions({ query: { runId: String(runId), limit: '100' } }),
    enabled: !!runId,
  });
  if (query.isError) console.error('useRunOutputs: failed to load run outputs', query.error);
  return query;
}

export function useDashboardOutputs() {
  return useQuery({
    ...queryOutputsOptions({ query: { visibility: 'dashboard', limit: '50' } }),
    refetchInterval: 30000, // refresh every 30s
  });
}

export function useSignalFeed(limit: number = 50) {
  return useQuery({
    ...queryOutputsOptions({ query: { visibility: 'feed', kind: 'signal', limit: String(limit) } }),
    refetchInterval: 10000, // refresh every 10s
  });
}

export function useOutputMetrics(workflowId: string | number, metricName: string, days: number = 30) {
  const from = new Date(Date.now() - days * 86400000).toISOString();
  return useQuery({
    ...getOutputMetricsOptions({ query: { workflowId: String(workflowId), metricName, from, limit: '500' } }),
    enabled: !!workflowId && !!metricName,
  });
}

export function useWidgetDefinitions() {
  return useQuery(listWidgetsOptions());
}

export function useDashboardWidgets(dashboardId?: string | number) {
  return useQuery({
    ...listPinnedWidgetsOptions({ query: { dashboardId: String(dashboardId) } }),
    enabled: !!dashboardId,
  });
}

// ==========================================
// Dashboard API hooks (multi-scope dashboards)
// ==========================================

export function useDashboards() {
  return useQuery(listDashboardsOptions());
}

/**
 * One dashboard with its widgets: the single cache entry every view of it reads — the generated `getDashboard` key,
 * shape `{ dashboard }` (the generated SDK's answer). The editor, the simplified view and the Strategy Tester share it.
 */
export function useDashboard(id: string | number | undefined) {
  const key = id === undefined || id === '' ? null : String(id);
  return useQuery({
    ...getDashboardOptions({ path: { id: key ?? '' } }),
    enabled: key !== null,
  });
}
